import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, Loader2, AlertTriangle } from "lucide-react";
import { useUpdateGasto, type BaseProrrateo, type GastoEmbarque } from "@/hooks/useGastosEmbarque";
import { useAuditTrail } from "@/hooks/useAuditTrail";
import { useQueryClient } from "@tanstack/react-query";
import { classifyExpense } from "@/lib/expenseClassifier";
import { toast } from "sonner";

const copFormatter = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

interface Props {
  gasto: GastoEmbarque | null;
  embarqueId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tasa fallback del sistema (TRM histórica) — solo lectura, para mostrar comparativa */
  systemRate?: number | null;
  /** Si el embarque está liquidado, los inputs van solo lectura */
  isLiquidado?: boolean;
}

export function EditGastoSheet({ gasto, embarqueId, open, onOpenChange, systemRate, isLiquidado }: Props) {
  const updateGasto = useUpdateGasto();
  const { logChange } = useAuditTrail();
  const qc = useQueryClient();

  const [concepto, setConcepto] = useState("");
  const [monto, setMonto] = useState("");
  const [moneda, setMoneda] = useState("COP");
  // ── Barrera 2: tercer estado nullable. La DB exige Peso/Valor, pero el frontend
  // permite null="Sin Clasificar" para forzar decisión humana cuando el clasificador
  // no tiene confianza. Submit queda disabled mientras base === null.
  const [base, setBase] = useState<BaseProrrateo | null>(null);
  const [trmPago, setTrmPago] = useState("");
  const [aplicaCosteo, setAplicaCosteo] = useState(true);
  const [incluirGravable, setIncluirGravable] = useState(false);
  const [notas, setNotas] = useState("");

  useEffect(() => {
    if (!gasto) return;
    setConcepto(gasto.concepto ?? "");
    setMonto(String(gasto.monto ?? ""));
    setMoneda(gasto.moneda ?? "COP");
    setBase((gasto.base_prorrateo as BaseProrrateo) ?? null);
    setTrmPago(gasto.trm_pago != null ? String(gasto.trm_pago) : "");
    setAplicaCosteo(gasto.aplica_costeo_inventario ?? true);
    setIncluirGravable(gasto.incluir_en_base_gravable ?? false);
    setNotas(gasto.notas ?? "");
  }, [gasto]);

  if (!gasto) return null;

  const trmPagoNum = trmPago ? parseFloat(trmPago) : null;
  const montoNum = parseFloat(monto) || 0;
  const effectiveRate =
    moneda === "COP" ? 1 : trmPagoNum && trmPagoNum > 0 ? trmPagoNum : systemRate ?? null;
  const previewCOP = effectiveRate ? montoNum * effectiveRate : null;
  const rateOrigin =
    moneda === "COP"
      ? "Moneda local (COP)"
      : trmPagoNum && trmPagoNum > 0
        ? "TRM de Pago Manual"
        : systemRate
          ? "TRM Histórica del Sistema"
          : "Sin TRM disponible";

  const handleSubmit = async () => {
    if (!concepto.trim()) {
      toast.error("El concepto es obligatorio");
      return;
    }
    if (!base) {
      toast.error("Debes clasificar la base de prorrateo (Peso, Valor o CBM) — requerido para compliance DIAN");
      return;
    }
    try {
      // TSK-210: Snapshot del estado previo (antes de la mutación)
      const before = {
        concepto: gasto.concepto ?? "",
        monto: String(gasto.monto ?? 0),
        moneda: gasto.moneda ?? "COP",
        base_prorrateo: gasto.base_prorrateo ?? "Valor",
        trm_pago: gasto.trm_pago != null ? String(gasto.trm_pago) : "",
        aplica_costeo: String(gasto.aplica_costeo_inventario ?? true),
        notas: gasto.notas ?? "",
      };
      const after = {
        concepto: concepto.trim(),
        monto: String(montoNum),
        moneda: moneda.toUpperCase(),
        base_prorrateo: base,
        trm_pago: trmPagoNum != null ? String(trmPagoNum) : "",
        aplica_costeo: String(aplicaCosteo),
        notas: notas.trim(),
      };

      await updateGasto.mutateAsync({
        id: gasto.id,
        embarque_id: embarqueId,
        concepto: after.concepto,
        monto: montoNum,
        moneda: after.moneda,
        base_prorrateo: base,
        trm_pago: trmPagoNum,
        aplica_costeo_inventario: aplicaCosteo,
        incluir_en_base_gravable: incluirGravable,
        notas: notas.trim() || null,
      });

      // TSK-210: Auditoría granular — un log por campo cambiado, en paralelo.
      // useAuditTrail filtra automáticamente cuando old === new.
      // Si el gasto está vinculado a un documento, los logs aparecen en el timeline
      // del documento padre; si es huérfano, quedan registrados sin ancla visual aún.
      const docId = gasto.documento_id ?? "";
      if (docId) {
        await Promise.all([
          logChange(docId, "gasto.concepto", before.concepto, after.concepto),
          logChange(docId, "gasto.monto", before.monto, after.monto),
          logChange(docId, "gasto.moneda", before.moneda, after.moneda),
          logChange(docId, "gasto.base_prorrateo", before.base_prorrateo, after.base_prorrateo),
          logChange(docId, "gasto.trm_pago", before.trm_pago, after.trm_pago),
          logChange(docId, "gasto.aplica_costeo", before.aplica_costeo, after.aplica_costeo),
          logChange(docId, "gasto.notas", before.notas, after.notas),
        ]);
      }

      // TSK-209: Invalidar embarques y landed cost
      qc.invalidateQueries({ queryKey: ["embarques"] });
      qc.invalidateQueries({ queryKey: ["embarque-documents", embarqueId] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["audit-logs", docId] });
      toast.success("Gasto actualizado. Recalculando Landed Cost…");
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Error al actualizar el gasto");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Editar Gasto
            {isLiquidado && (
              <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-700">
                Liquidado · solo lectura
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription className="text-xs">
            Ajuste granular del gasto. La <strong>TRM de Pago</strong> sobrescribe la tasa histórica del sistema
            para este registro únicamente.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="g-concepto" className="text-xs">Concepto</Label>
            <Input
              id="g-concepto"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              disabled={isLiquidado}
              className="h-9 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="g-monto" className="text-xs">Monto</Label>
              <Input
                id="g-monto"
                type="number"
                step="0.01"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                disabled={isLiquidado}
                className="h-9 text-sm tabular-nums"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="g-moneda" className="text-xs">Moneda</Label>
              <Input
                id="g-moneda"
                value={moneda}
                onChange={(e) => setMoneda(e.target.value.toUpperCase())}
                maxLength={3}
                disabled={isLiquidado}
                className="h-9 text-sm uppercase"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-2">
              Base de Prorrateo
              {base === null && (
                <Badge variant="outline" className="h-4 px-1 text-[9px] border-amber-500/60 text-amber-700 dark:text-amber-400 gap-1">
                  <AlertTriangle className="h-2.5 w-2.5" /> Sin clasificar
                </Badge>
              )}
              {(() => {
                const sug = classifyExpense(concepto);
                if (!sug.suggested || sug.suggested === base) return null;
                return (
                  <button
                    type="button"
                    onClick={() => {
                      setBase(sug.suggested!);
                      if (sug.forceGravable) setIncluirGravable(true);
                    }}
                    disabled={isLiquidado}
                    className="inline-flex items-center gap-1 rounded-sm border border-primary/40 bg-primary/5 px-1.5 py-0.5 text-[9px] font-medium text-primary hover:bg-primary/10 transition disabled:opacity-50"
                    title={`Sugerencia automática basada en "${sug.matchedKeyword}"${sug.forceGravable ? " — Gasto en origen, gravable DIAN" : ""}`}
                  >
                    Sugerido: {sug.suggested}{sug.forceGravable ? " ⚖" : ""}
                  </button>
                );
              })()}
            </Label>
            <Select
              value={base ?? ""}
              onValueChange={(v: BaseProrrateo) => setBase(v)}
              disabled={isLiquidado}
            >
              <SelectTrigger className={`h-9 text-sm ${base === null ? "border-amber-500/60" : ""}`}>
                <SelectValue placeholder="⚠ Selecciona Peso, Valor o CBM" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Valor">Valor (FOB)</SelectItem>
                <SelectItem value="Peso">Peso (Kg)</SelectItem>
                <SelectItem value="CBM">CBM (Volumen)</SelectItem>
              </SelectContent>
            </Select>
            {base === null && (
              <p className="text-[10px] text-amber-700 dark:text-amber-400 leading-snug">
                Compliance DIAN requiere base de prorrateo explícita.
              </p>
            )}
          </div>

          <Separator />

          {/* TSK-209: Campo clave — TRM de Pago Exacta */}
          <div className="space-y-1.5">
            <Label htmlFor="g-trm" className="text-xs flex items-center gap-1.5">
              TRM de Pago Exacta
              <span className="text-[10px] text-muted-foreground font-normal">(Opcional)</span>
            </Label>
            <Input
              id="g-trm"
              type="number"
              step="0.0001"
              placeholder={systemRate ? `Sistema: ${systemRate.toLocaleString()}` : "Sin TRM histórica"}
              value={trmPago}
              onChange={(e) => setTrmPago(e.target.value)}
              disabled={isLiquidado || moneda === "COP"}
              className="h-9 text-sm tabular-nums"
            />
            <p className="text-[11px] text-muted-foreground leading-snug">
              Si se define, sobrescribe la TRM histórica al convertir este gasto a COP. Útil para registrar la tasa
              exacta del banco al momento del pago.
            </p>
          </div>

          {/* Preview de conversión */}
          {moneda !== "COP" && (
            <Alert className="py-2.5">
              <Info className="h-3.5 w-3.5" />
              <AlertDescription className="text-xs space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Equivalente COP:</span>
                  <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
                    {previewCOP != null ? copFormatter.format(previewCOP) : "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Tasa aplicada:</span>
                  <span className="font-mono text-[11px]">
                    {effectiveRate ? `× ${effectiveRate.toLocaleString()}` : "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Origen:</span>
                  <span className="text-[11px] font-medium">{rateOrigin}</span>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs">Aplica al Costeo de Inventario</Label>
              <p className="text-[11px] text-muted-foreground">Si está apagado, el gasto no entra al prorrateo.</p>
            </div>
            <Switch checked={aplicaCosteo} onCheckedChange={setAplicaCosteo} disabled={isLiquidado} />
          </div>

          <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 p-3">
            <div className="pr-3">
              <Label className="text-xs">Incluir en Base Gravable DIAN</Label>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Solo enciende esto para <strong>flete y seguro internacional</strong>. Suma a la base sobre la que se calcula <strong>Arancel + IVA</strong>. Apagado por defecto (postura conservadora).
              </p>
            </div>
            <Switch checked={incluirGravable} onCheckedChange={setIncluirGravable} disabled={isLiquidado} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="g-notas" className="text-xs">Notas</Label>
            <Input
              id="g-notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              disabled={isLiquidado}
              className="h-9 text-sm"
            />
          </div>
        </div>

        <SheetFooter className="mt-6 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLiquidado || updateGasto.isPending || base === null}
            className="w-full sm:w-auto"
          >
            {updateGasto.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Guardar cambios
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
